import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { AuthContext } from '../contexts/AuthContext';
import {
  appPagePath,
  canAccess,
  fetchManualPage,
  getApp,
  markReleaseNotesSeen,
  releaseNotePath,
  useManualIndex,
} from '../manual';
import ManualAsk from '../components/manual/ManualAsk';
import ManualImage from '../components/manual/ManualImage';
import '../styles/Help.css';

const NOTES_PAGE_SIZE = 5;

const formatDate = (isoDate) =>
  new Date(`${isoDate}T00:00:00`).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

const MarkdownLink = ({ href = '', children }) => {
  if (href.startsWith('/')) {
    return <Link to={href}>{children}</Link>;
  }
  return (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
};

const useManualFile = (path) => {
  const [state, setState] = useState({ status: 'loading', content: '' });

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading', content: '' });
    fetchManualPage(path)
      .then((content) => {
        if (!cancelled) setState({ status: 'ready', content });
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'error', content: '' });
      });
    return () => {
      cancelled = true;
    };
  }, [path]);

  return state;
};

const ManualMarkdown = ({ path }) => {
  const { status, content } = useManualFile(path);

  if (status === 'loading') {
    return (
      <div className="text-muted py-3">
        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
        Loading…
      </div>
    );
  }
  if (status === 'error') {
    return <div className="alert alert-warning mb-0">This page couldn't be loaded. Try refreshing.</div>;
  }
  return (
    <div className="manual-content">
      <ReactMarkdown remarkPlugins={[remarkGfm]} skipHtml components={{ a: MarkdownLink, img: ManualImage }}>
        {content}
      </ReactMarkdown>
    </div>
  );
};

const Breadcrumb = ({ current }) => (
  <nav aria-label="breadcrumb">
    <ol className="breadcrumb mb-3">
      <li className="breadcrumb-item">
        <Link to="/help">Help</Link>
      </li>
      <li className="breadcrumb-item active" aria-current="page">
        {current}
      </li>
    </ol>
  </nav>
);

const AppCard = ({ app, hasAccess }) => (
  <div className="col">
    <Link to={`/help/${app.id}`} className={`card h-100 manual-app-card text-decoration-none ${hasAccess ? '' : 'manual-app-card-locked'}`}>
      <div className="card-body">
        <h3 className="h6 card-title d-flex align-items-center mb-2">
          <i className={`bi ${app.icon} me-2`}></i>
          {app.title}
        </h3>
        <p className="card-text small mb-2">{app.summary}</p>
        <p className="card-text small text-muted mb-0">
          <i className="bi bi-people me-1"></i>
          {app.audience}
        </p>
      </div>
      {!hasAccess && (
        <div className="card-footer small text-muted bg-transparent">
          <i className="bi bi-lock me-1"></i>
          {app.route ? 'Ask an account administrator for access' : 'Opened by clients from a link'}
        </div>
      )}
    </Link>
  </div>
);

const HelpIndex = ({ index }) => {
  const auth = useContext(AuthContext);
  const [query, setQuery] = useState('');
  const latest = index.releaseNotes[0];

  const { yours, others } = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const matches = index.apps.filter(
      (app) => !needle || [app.title, app.summary, app.audience].some((text) => text.toLowerCase().includes(needle))
    ).sort((a, b) => a.title.localeCompare(b.title));
    return {
      yours: matches.filter((app) => canAccess(app, auth)),
      others: matches.filter((app) => !canAccess(app, auth)),
    };
  }, [query, auth, index]);

  return (
    <div className="container-fluid manual-page">
      <div className="d-flex flex-wrap align-items-end justify-content-between gap-3 mb-4">
        <div>
          <h1 className="h3 mb-1">
            <i className="bi bi-question-circle me-2"></i>
            Help &amp; What's New
          </h1>
          <p className="text-muted mb-0">What each app does, who it's for, and how to get started.</p>
        </div>
        <input
          type="search"
          className="form-control manual-search"
          placeholder="Filter apps…"
          aria-label="Filter apps"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      <ManualAsk index={index} />

      {latest && (
        <div className="card border-primary mb-4">
          <div className="card-body d-flex flex-wrap align-items-center justify-content-between gap-2">
            <div>
              <div className="small text-primary fw-semibold mb-1">What's new · {formatDate(latest.date)}</div>
              <div className="fw-semibold">{latest.title}</div>
              <div className="small text-muted">{latest.summary}</div>
            </div>
            <Link to="/help/whats-new" className="btn btn-outline-primary btn-sm">
              Read release notes
            </Link>
          </div>
        </div>
      )}

      <section aria-labelledby="help-your-apps">
        <h2 id="help-your-apps" className="h5 mb-3">
          Your apps
        </h2>
        {yours.length ? (
          <div className="row row-cols-1 row-cols-md-2 row-cols-xl-3 g-3 mb-4">
            {yours.map((app) => (
              <AppCard key={app.id} app={app} hasAccess />
            ))}
          </div>
        ) : (
          <p className="text-muted mb-4">{query ? 'No matching apps.' : "You don't have access to any apps yet."}</p>
        )}
      </section>

      {others.length > 0 && (
        <section aria-labelledby="help-other-apps">
          <h2 id="help-other-apps" className="h5 mb-1">
            Other apps
          </h2>
          <p className="small text-muted mb-3">You don't have these yet. Open one to see what it does and how to get access.</p>
          <div className="row row-cols-1 row-cols-md-2 row-cols-xl-3 g-3">
            {others.map((app) => (
              <AppCard key={app.id} app={app} hasAccess={false} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

const HelpAppPage = ({ index }) => {
  const auth = useContext(AuthContext);
  const { appId } = useParams();
  const app = getApp(index, appId);

  if (!app) {
    return (
      <div className="container-fluid manual-page">
        <Breadcrumb current="Not found" />
        <div className="alert alert-warning">We couldn't find that help page.</div>
      </div>
    );
  }

  const hasAccess = canAccess(app, auth);

  return (
    <div className="container-fluid manual-page">
      <Breadcrumb current={app.title} />
      <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-3">
        <div>
          <h1 className="h3 mb-1">
            <i className={`bi ${app.icon} me-2`}></i>
            {app.title}
          </h1>
          <p className="lead fs-6 mb-2">{app.summary}</p>
          <span className="badge text-bg-light border">
            <i className="bi bi-people me-1"></i>
            {app.audience}
          </span>
        </div>
        {app.route && hasAccess && (
          <Link to={app.route} className="btn btn-primary">
            Open {app.title}
            <i className="bi bi-arrow-right ms-2"></i>
          </Link>
        )}
        {app.route && !hasAccess && (
          <span className="badge text-bg-secondary p-2">
            <i className="bi bi-lock me-1"></i>
            You don't have access yet. See "Getting access" below.
          </span>
        )}
        {!app.route && (
          <span className="badge text-bg-info p-2">
            <i className="bi bi-link-45deg me-1"></i>
            Client-facing: opened from a link
          </span>
        )}
      </div>
      <div className="card mb-4">
        <div className="card-body">
          <ManualMarkdown path={appPagePath(app)} />
        </div>
      </div>
      <h2 className="h6 text-muted">Still have a question about {app.title}?</h2>
      <ManualAsk index={index} appId={app.id} compact />
    </div>
  );
};

const ReleaseNote = ({ note, index }) => (
  <div className="card mb-3">
    <div className="card-body">
      <div className="small text-muted mb-1">{formatDate(note.date)}</div>
      <h2 className="h5 mb-2">{note.title}</h2>
      {note.apps.length > 0 && (
        <div className="mb-2">
          {note.apps.map((id) => {
            const app = getApp(index, id);
            return app ? (
              <Link key={id} to={`/help/${id}`} className="badge text-bg-light border text-decoration-none me-1">
                {app.title}
              </Link>
            ) : null;
          })}
        </div>
      )}
      <ManualMarkdown path={releaseNotePath(note)} />
    </div>
  </div>
);

const WhatsNew = ({ index }) => {
  const [visible, setVisible] = useState(NOTES_PAGE_SIZE);
  const notes = index.releaseNotes;

  useEffect(() => {
    markReleaseNotesSeen(index);
  }, [index]);

  return (
    <div className="container-fluid manual-page">
      <Breadcrumb current="What's New" />
      <h1 className="h3 mb-3">
        <i className="bi bi-stars me-2"></i>
        What's New
      </h1>
      {notes.length === 0 && <p className="text-muted">No release notes yet.</p>}
      {notes.slice(0, visible).map((note) => (
        <ReleaseNote key={note.id} note={note} index={index} />
      ))}
      {visible < notes.length && (
        <button type="button" className="btn btn-outline-secondary" onClick={() => setVisible(visible + NOTES_PAGE_SIZE)}>
          Show older release notes
        </button>
      )}
    </div>
  );
};

const Help = ({ view }) => {
  const { status, index } = useManualIndex();

  if (status === 'loading') {
    return (
      <div className="container-fluid manual-page text-muted py-3">
        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
        Loading help…
      </div>
    );
  }
  if (status === 'error') {
    return (
      <div className="container-fluid manual-page">
        <div className="alert alert-warning">The help pages couldn't be loaded. Try refreshing.</div>
      </div>
    );
  }
  if (view === 'whats-new') return <WhatsNew index={index} />;
  if (view === 'app') return <HelpAppPage index={index} />;
  return <HelpIndex index={index} />;
};

export default Help;
